<template>
  <ac-modal :model-value="true" mobile-modal-type="bottomSheet" @update:modelValue="onModelValue">
    <div
      class="builderModal"
      data-test="sheet"
      :style="sheetStyle"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
    >
      <builder-modal-skeleton v-if="phase === 'skeleton'" :show-close="content.showClose" @close="emit('close', 'close')" />

      <template v-else>
        <div v-if="content.imageUrl" :class="['builderModal_image', { '-loaded': imageLoaded }]" data-test="image" data-drag-zone>
          <img v-if="imageLoaded" class="builderModal_img" :src="content.imageUrl" alt="">
          <span v-if="rule.isDraft" class="builderModal_draft" data-test="draft">черновик</span>
          <builder-modal-close v-if="content.showClose" data-test="close" @click="emit('close', 'close')" />
        </div>
        <div v-else class="builderModal_top" data-drag-zone>
          <span v-if="rule.isDraft" class="builderModal_draft" data-test="draft">черновик</span>
          <builder-modal-close v-if="content.showClose" on-white data-test="close" @click="emit('close', 'close')" />
        </div>

        <div class="builderModal_body">
          <div v-if="content.heading" class="builderModal_heading" data-test="heading">{{ content.heading }}</div>
          <div v-if="safeText" class="builderModal_text" data-test="text" v-html="safeText" />
          <div v-if="content.buttons.length" class="builderModal_buttons">
            <ac-button
              v-for="button in content.buttons"
              :key="button.id"
              block
              :variant="AC_BUTTON_VARIANT[button.style]"
              :class="['builderModal_button', `-${button.style}`]"
              data-test="button"
              @click="emit('cta', button)"
            >
              {{ button.text }}
            </ac-button>
          </div>
        </div>
      </template>
    </div>
  </ac-modal>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import type { PropType } from 'vue'
import { useMq } from 'vue3-mq'

import AcButton from '@ui-kit/components/AcButton.vue'
import AcModal from '@ui-kit/components/AcModal.vue'
import { IMAGE_TIMEOUT_MS } from '../constants'
import { preloadImage } from '../imagePreload'
import { sanitizeHtml } from '../sanitizeHtml'
import type { ButtonStyle, CloseMethod, ModalButton, ModalRule } from '../types'
import BuilderModalClose from './BuilderModalClose.vue'
import BuilderModalSkeleton from './BuilderModalSkeleton.vue'

// Свайп вниз дальше этого — закрытие шторки (как тап по оверлею).
const SWIPE_CLOSE_DISTANCE = 80

// Каталог кнопок конструктора → вариант AcButton. Чего у AcButton нет (цвета ghost/alert/warning,
// акцентный текст ghost_stroke) — переопределяется ниже в стилях этого компонента, не в AcButton.
const AC_BUTTON_VARIANT: Record<ButtonStyle, 'outlined' | 'text' | undefined> = {
  active: undefined,
  secondary: 'outlined',
  ghost_stroke: 'text',
  ghost: undefined,
  alert: undefined,
  warning: undefined
}

const props = defineProps({
  rule: { type: Object as PropType<ModalRule>, required: true },
  loadImage: { type: Function as PropType<(url: string) => Promise<boolean>>, default: preloadImage },
  imageTimeoutMs: { type: Number, default: IMAGE_TIMEOUT_MS }
})

const emit = defineEmits<{
  shown: []
  close: [method: CloseMethod]
  cta: [button: ModalButton]
}>()

const mq = useMq()
const content = computed(() => props.rule.content)
const safeText = computed(() => (content.value.text ? sanitizeHtml(content.value.text) : ''))

// skeleton → ready. ready = видны текст и кнопки; ровно в этот момент — shown.
const phase = ref<'skeleton' | 'ready'>('skeleton')
const imageLoaded = ref(false)
let timer: ReturnType<typeof setTimeout> | undefined

function toReady (): void {
  if (phase.value === 'ready') return
  phase.value = 'ready'
  emit('shown')
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
  const url = content.value.imageUrl
  if (!url) {
    toReady()
    return
  }
  timer = setTimeout(toReady, props.imageTimeoutMs)
  props.loadImage(url)
    .catch(() => false)
    .then(ok => {
      imageLoaded.value = ok
      toReady()
    })
})

function onKeydown (e: KeyboardEvent): void {
  if (e.key === 'Escape') emit('close', 'overlay')
}

onBeforeUnmount(() => {
  if (timer) clearTimeout(timer)
  document.removeEventListener('keydown', onKeydown)
})

// AcModal шлёт update:modelValue=false по клику на оверлей.
function onModelValue (value: boolean): void {
  if (!value) emit('close', 'overlay')
}

// Свайп — только у шторки (мобильная ширина).
const dragStartY = ref<number | null>(null)
const dragOffset = ref(0)
const sheetStyle = computed(() => (dragOffset.value > 0 ? { transform: `translateY(${dragOffset.value}px)`, transition: 'none' } : undefined))

function onPointerDown (e: PointerEvent): void {
  if (mq.current === 'desktop') return
  if (!(e.target instanceof Element) || !e.target.closest('[data-drag-zone]')) return
  dragStartY.value = e.clientY
}

function onPointerMove (e: PointerEvent): void {
  if (dragStartY.value === null) return
  dragOffset.value = Math.max(0, e.clientY - dragStartY.value)
}

function onPointerUp (): void {
  if (dragStartY.value === null) return
  const shouldClose = dragOffset.value > SWIPE_CLOSE_DISTANCE
  dragStartY.value = null
  dragOffset.value = 0
  if (shouldClose) emit('close', 'overlay')
}
</script>

<style lang="scss" scoped>
@import '@web/assets/scss/textContents.scss';
@import './builderModal';

.builderModal {
  position: relative;
  width: $bm-desktop-width;
  overflow: hidden;
  border-radius: $bm-radius;
  background: var(--ac-color-white);
  transition: transform $bm-swipe-transition;

  // Свайп-закрытие стартует только из верхней зоны; остальное — обычный скролл шторки.
  :deep([data-drag-zone]) {
    touch-action: none;
  }

  @media (max-width: $screen-desktop) {
    width: 100%;
    max-height: 100dvh;
    overflow-y: auto;
    border-radius: $bm-radius $bm-radius 0 0;
  }

  &_image {
    position: relative;
    height: $bm-desktop-image-height;
    // Плейсхолдер: тот же градиент, что в скелетоне.
    background: linear-gradient(180deg, rgba(var(--ac-color-rgb-deep-liliac), .35) 0%, var(--ac-color-white) 100%);

    @media (max-width: $screen-desktop) {
      height: auto;
      aspect-ratio: $bm-sheet-image-ratio;
    }

    // Нижний край картинки плавно переходит в белый фон.
    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: $bm-image-fade-height;
      background: linear-gradient(180deg, rgba(var(--ac-color-rgb-white), 0) 0%, var(--ac-color-white) 100%);
    }
  }

  &_img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    animation: builderModalFadeIn $bm-swipe-transition ease-out;
  }

  &_top {
    position: relative;
    height: $bm-close-size + $bm-close-offset * 2;
  }

  &_draft {
    @extend .text-xs-medium;

    position: absolute;
    top: $bm-close-offset;
    left: $bm-close-offset;
    z-index: 1;
    padding: 0 $bm-skeleton-bar-gap;
    border-radius: $bm-skeleton-radius;
    background: var(--ac-color-orange);
    color: var(--ac-color-white);
  }

  &_body {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 $bm-content-padding $bm-bottom-padding;
    text-align: center;

    @media (max-width: $screen-desktop) {
      padding-bottom: calc(#{$bm-bottom-padding} + env(safe-area-inset-bottom));
    }
  }

  &_heading {
    @extend .text-2xl-medium;

    color: var(--ac-color-deep-navy-blue);
    overflow-wrap: anywhere;

    @media (max-width: $screen-desktop) {
      @include text-size(20px, 1.5, 500);
    }
  }

  &_text {
    @extend .text-sm-compact;

    margin-top: $bm-gap-heading;
    color: var(--ac-color-dark-gray);
    overflow-wrap: anywhere;

    @media (max-width: $screen-desktop) {
      @include text-size(14px, 1.43);
    }

    :deep(p) {
      margin: 0;
    }

    :deep(ul),
    :deep(ol) {
      margin: 0;
      padding-left: $bm-content-padding;
      text-align: left;
    }
  }

  &_buttons {
    display: flex;
    flex-direction: column;
    gap: $bm-button-gap;
    width: 100%;
    margin-top: $bm-gap-buttons;
  }

  // Переопределения AcButton под каталог кнопок конструктора — только здесь, сам AcButton не трогаем.
  &_button {
    &.-ghost_stroke {
      color: var(--ac-color-deep-liliac);
    }

    &.-ghost {
      background-color: var(--ac-color-background);
      color: var(--ac-color-dark-gray);
    }

    &.-alert {
      background-color: var(--ac-color-red);
    }

    &.-warning {
      background-color: var(--ac-color-orange);
    }
  }
}

@keyframes builderModalFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
